import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Flag, X, MapPin, Phone, Truck, Check } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime, formatCurrency } from '../lib/utils'

type FilterStatus = 'open' | 'resolved' | 'all'
type FilterReporter = 'all' | 'driver' | 'customer'

interface Issue {
  id: string
  order_id: string
  driver_id: string | null
  user_id: string | null
  reporter_type: 'driver' | 'customer'
  issue_type: string
  notes: string | null
  photo_keys: string[]
  status: 'open' | 'resolved'
  created_at: string
  resolved_at: string | null
  resolution_notes: string | null
  order_number: string
  order_status: string
  delivery_address: string | null
  delivery_phone: string | null
  order_total_fils: number
  driver_name: string | null
  driver_phone: string | null
  customer_name: string | null
  customer_phone: string | null
  resolved_by_name: string | null
}

const ISSUE_LABELS: Record<string, string> = {
  // Ambassador-side
  customer_unreachable: 'Customer not answering',
  customer_not_present: 'Customer not at address',
  customer_underage:    'Customer underage / no ID',
  customer_refused:     'Customer refused order',
  address_problem:      'Wrong / unfindable address',
  items_problem:        'Items damaged or missing',
  vehicle_problem:      'Vehicle problem',
  // Customer-side
  items_missing:        'Items missing',
  items_damaged:        'Items damaged',
  wrong_items:          'Wrong items received',
  ambassador_problem:   'Ambassador problem',
  // Shared
  other:                'Other',
}

const ISSUE_COLORS: Record<string, string> = {
  customer_unreachable: 'bg-amber-500/15  text-amber-400  border-amber-500/30',
  customer_not_present: 'bg-amber-500/15  text-amber-400  border-amber-500/30',
  customer_underage:    'bg-[#C41E3A]/15  text-[#C41E3A]  border-[#C41E3A]/30',
  customer_refused:     'bg-[#C41E3A]/15  text-[#C41E3A]  border-[#C41E3A]/30',
  address_problem:      'bg-orange-500/15 text-orange-400 border-orange-500/30',
  items_problem:        'bg-[#C41E3A]/15  text-[#C41E3A]  border-[#C41E3A]/30',
  vehicle_problem:      'bg-orange-500/15 text-orange-400 border-orange-500/30',
  items_missing:        'bg-[#C41E3A]/15  text-[#C41E3A]  border-[#C41E3A]/30',
  items_damaged:        'bg-[#C41E3A]/15  text-[#C41E3A]  border-[#C41E3A]/30',
  wrong_items:          'bg-[#C41E3A]/15  text-[#C41E3A]  border-[#C41E3A]/30',
  ambassador_problem:   'bg-amber-500/15  text-amber-400  border-amber-500/30',
  other:                'bg-gray-500/15   text-gray-400   border-gray-500/30',
}

export default function Issues() {
  const [filter, setFilter] = useState<FilterStatus>('open')
  const [reporter, setReporter] = useState<FilterReporter>('all')
  const [resolving, setResolving] = useState<Issue | null>(null)
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<{ issues: Issue[] }>({
    queryKey: ['admin-issues', filter, reporter],
    queryFn: () => api.get(`/issues?status=${filter}&reporter=${reporter}`).then(r => r.data),
    refetchInterval: 30_000, // soft real-time
  })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-white">Issues</h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Problems reported by ambassadors during delivery, or customers post-delivery. Resolve once handled.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ReporterFilter value={reporter} onChange={setReporter} />
          <FilterTabs value={filter} onChange={setFilter} />
        </div>
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="px-4 py-12 text-center text-gray-600 text-sm">Loading…</div>
        ) : !data?.issues.length ? (
          <div className="px-4 py-16 text-center text-gray-600 text-sm">
            {filter === 'open'
              ? '✓ No open issues. Everything is running smoothly.'
              : 'No issues to show.'}
          </div>
        ) : (
          <div className="divide-y divide-[#222]">
            {data.issues.map(i => (
              <IssueRow key={i.id} issue={i} onResolve={() => setResolving(i)} />
            ))}
          </div>
        )}
      </div>

      {resolving && (
        <ResolveModal
          issue={resolving}
          onClose={() => setResolving(null)}
          onResolved={() => {
            qc.invalidateQueries({ queryKey: ['admin-issues'] })
            qc.invalidateQueries({ queryKey: ['issues-open-count'] })
            setResolving(null)
          }}
        />
      )}
    </div>
  )
}

function FilterTabs({ value, onChange }: { value: FilterStatus; onChange: (v: FilterStatus) => void }) {
  const tabs: { id: FilterStatus; label: string }[] = [
    { id: 'open',     label: 'Open' },
    { id: 'resolved', label: 'Resolved' },
    { id: 'all',      label: 'All' },
  ]
  return (
    <div className="inline-flex bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg p-0.5">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value === t.id ? 'bg-[#C41E3A] text-white' : 'text-gray-400 hover:text-white'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

function ReporterFilter({ value, onChange }: { value: FilterReporter; onChange: (v: FilterReporter) => void }) {
  const tabs: { id: FilterReporter; label: string }[] = [
    { id: 'all',      label: 'Both' },
    { id: 'driver',   label: 'Ambassador' },
    { id: 'customer', label: 'Customer' },
  ]
  return (
    <div className="inline-flex bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg p-0.5">
      {tabs.map(t => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
            value === t.id ? 'bg-[#C41E3A] text-white' : 'text-gray-400 hover:text-white'
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  )
}

function IssueRow({ issue, onResolve }: { issue: Issue; onResolve: () => void }) {
  const isOpen = issue.status === 'open'
  const isCustomerReport = issue.reporter_type === 'customer'
  return (
    <div className="px-4 py-4">
      <div className="flex items-start gap-3 flex-wrap">
        {/* Type pill */}
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${ISSUE_COLORS[issue.issue_type] ?? ISSUE_COLORS.other}`}>
          <Flag size={10} className="inline mr-1 -mt-0.5" />
          {ISSUE_LABELS[issue.issue_type] ?? issue.issue_type}
        </span>

        {/* Reporter pill */}
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          isCustomerReport
            ? 'bg-blue-500/15 text-blue-400'
            : 'bg-purple-500/15 text-purple-400'
        }`}>
          {isCustomerReport ? 'From customer' : 'From ambassador'}
        </span>

        <span className={`text-xs px-2 py-0.5 rounded-full ${
          isOpen ? 'bg-orange-500/15 text-orange-400' : 'bg-green-500/15 text-green-400'
        }`}>
          {isOpen ? 'Open' : 'Resolved'}
        </span>

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-gray-600">{formatDateTime(issue.created_at)}</span>
          {isOpen && (
            <button
              onClick={onResolve}
              className="flex items-center gap-1.5 text-xs bg-green-500/15 hover:bg-green-500/25 text-green-400 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Check size={13} /> Mark resolved
            </button>
          )}
        </div>
      </div>

      {/* Order + ambassador context */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <div className="bg-white/[0.02] border border-[#222] rounded-lg p-3">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-1.5">Order</p>
          <p className="text-sm text-white">#{issue.order_number}</p>
          <p className="text-xs text-gray-500 mt-0.5 capitalize">
            {issue.order_status.replace(/_/g, ' ')} · {formatCurrency(issue.order_total_fils)}
          </p>
          {issue.delivery_address && (
            <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1.5">
              <MapPin size={11} className="text-gray-600 mt-0.5 shrink-0" />
              <span>{issue.delivery_address}</span>
            </p>
          )}
          {issue.delivery_phone && (
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
              <Phone size={11} className="text-gray-600" />
              <a
                href={`tel:${issue.delivery_phone}`}
                className="text-blue-400 hover:underline font-mono"
              >{issue.delivery_phone}</a>
            </p>
          )}
        </div>
        <div className="bg-white/[0.02] border border-[#222] rounded-lg p-3">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-1.5">
            {isCustomerReport ? 'Reported by (customer)' : 'Reported by (ambassador)'}
          </p>
          {isCustomerReport ? (
            <>
              <p className="text-sm text-white">{issue.customer_name ?? '—'}</p>
              {issue.customer_phone && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                  <Phone size={11} className="text-gray-600" />
                  <a
                    href={`tel:${issue.customer_phone}`}
                    className="text-blue-400 hover:underline font-mono"
                  >{issue.customer_phone}</a>
                </p>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-white flex items-center gap-1.5">
                <Truck size={12} className="text-gray-500" />
                {issue.driver_name ?? '—'}
              </p>
              {issue.driver_phone && (
                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                  <Phone size={11} className="text-gray-600" />
                  <a
                    href={`tel:${issue.driver_phone}`}
                    className="text-blue-400 hover:underline font-mono"
                  >{issue.driver_phone}</a>
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Notes from the reporter */}
      {issue.notes && (
        <div className="mt-3 px-3 py-2 bg-white/[0.02] border border-[#222] rounded-lg">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-1">
            {isCustomerReport ? 'Customer notes' : 'Ambassador notes'}
          </p>
          <p className="text-sm text-gray-300 whitespace-pre-wrap">{issue.notes}</p>
        </div>
      )}

      {/* Photos attached by the customer. Up to 3, click opens the
          original in a new tab — no lightbox yet; simple is fine since
          ops view counts are low. */}
      {issue.photo_keys && issue.photo_keys.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest mb-2">
            Photos ({issue.photo_keys.length})
          </p>
          <div className="flex gap-2 flex-wrap">
            {issue.photo_keys.map((key, i) => (
              <a
                key={key}
                href={`https://files.drinx.jo/${key}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-20 h-20 rounded-lg overflow-hidden border border-[#222] hover:border-[#C41E3A]/50 transition-colors"
                title={`Photo ${i + 1} — click to open full size`}
              >
                <img
                  src={`https://files.drinx.jo/${key}`}
                  alt={`Issue photo ${i + 1}`}
                  className="w-full h-full object-cover"
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Resolution */}
      {!isOpen && (
        <div className="mt-3 px-3 py-2 bg-green-500/5 border border-green-500/20 rounded-lg">
          <p className="text-[10px] text-green-400 uppercase tracking-widest mb-1">
            Resolved {issue.resolved_at && formatDateTime(issue.resolved_at)}
            {issue.resolved_by_name && ` by ${issue.resolved_by_name}`}
          </p>
          {issue.resolution_notes && (
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{issue.resolution_notes}</p>
          )}
        </div>
      )}
    </div>
  )
}

function ResolveModal({
  issue, onClose, onResolved,
}: {
  issue: Issue
  onClose: () => void
  onResolved: () => void
}) {
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => api.patch(`/issues/${issue.id}/resolve`, notes ? { notes } : {}),
    onSuccess: () => onResolved(),
    onError: (e: { response?: { data?: { message?: string } } }) =>
      setError(e.response?.data?.message ?? 'Failed to resolve'),
  })

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2e2e2e]">
          <h2 className="text-sm font-semibold text-white">Resolve issue</h2>
          <button onClick={onClose}><X size={16} className="text-gray-400" /></button>
        </div>
        <div className="p-5">
          <p className="text-xs text-gray-500 mb-3">
            <span className="text-gray-300">{ISSUE_LABELS[issue.issue_type] ?? issue.issue_type}</span>
            {' · Order #'}{issue.order_number}
          </p>
          <label className="block text-xs text-gray-400 mb-1.5">Resolution notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="What was done?"
            rows={3}
            maxLength={1000}
            className="w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          />
          {error && <p className="text-xs text-red-400 mt-2">{error}</p>}
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-400 hover:text-white"
            >Cancel</button>
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="px-4 py-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {mutation.isPending ? 'Resolving…' : 'Mark resolved'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
